/**
 * SKILL CHALLENGES + PERSONAL BESTS — the "one more try" loop for solo practice.
 *
 * Design intent (owner: "make the game addictive"): a challenge is a short,
 * scored burst with a visible personal best. The PB you JUST missed is the
 * strongest retrigger there is, so the engine keeps score, streak and PB in the
 * same glance, and a run is always one keypress from restarting.
 *
 * Deliberately signal-light: the engine consumes only what solo practice
 * already produces — a goal happened, a drill reset happened without a goal
 * (miss), time passed. No new sim events, no shot-position tracking; that keeps
 * it honest under the training supervisor, which force-resets the world after
 * every attempt.
 *
 * Pure logic + an injectable store (localStorage in the browser, a Map in
 * tests). Persistence is per challenge id AND keeper difficulty — a PB set
 * against the beginner keeper must never overwrite one set against the pro.
 */

export interface ChallengeDef {
  id: string;
  name: string;
  /** One-line rule, shown in the panel. */
  rule: string;
  /** Run length in seconds; 0 = untimed (streak-style, ends on first miss). */
  durationS: number;
}

export const CHALLENGES: readonly ChallengeDef[] = [
  { id: 'blitz60', name: 'Blitz 60', rule: 'Score as many as you can in 60 s', durationS: 60 },
  { id: 'streak', name: 'Hot Streak', rule: 'Consecutive goals — first miss ends the run', durationS: 0 },
  { id: 'rapid30', name: 'Rapid 30', rule: 'Score as many as you can in 30 s', durationS: 30 },
];

/** Date-seeded daily pick, stable for the whole calendar day (local time) so
 *  everyone faces the same challenge and a PB means the same thing all day. */
export function dailyChallenge(now: Date): ChallengeDef {
  const day = now.getFullYear() * 372 + now.getMonth() * 31 + now.getDate();
  return CHALLENGES[day % CHALLENGES.length]!;
}

export interface ChallengeState {
  def: ChallengeDef;
  running: boolean;
  /** Seconds left (timed) — meaningless for untimed defs. */
  timeLeftS: number;
  score: number;
  /** Current consecutive-goal streak within this run. */
  streak: number;
  /** True once the run has ended (time up / streak broken). */
  finished: boolean;
  /** PB for this def+difficulty BEFORE this run started. */
  previousBest: number;
  /** Set when the run finishes above the previous best. */
  isNewBest: boolean;
}

/** Minimal storage seam — localStorage in the app, a Map in tests. */
export interface PbStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export function localStoragePbStore(): PbStore {
  return {
    get: (k) => {
      try { return globalThis.localStorage?.getItem(k) ?? null; } catch { return null; }
    },
    set: (k, v) => {
      try { globalThis.localStorage?.setItem(k, v); } catch { /* private mode — PBs just don't persist */ }
    },
  };
}

const KEY_PREFIX = 'fg.challenge.pb.';

export class ChallengeEngine {
  private state: ChallengeState | null = null;
  constructor(private readonly store: PbStore) {}

  /** PB for a challenge at a difficulty, 0 if none yet. */
  best(defId: string, difficulty: string): number {
    const raw = this.store.get(`${KEY_PREFIX}${defId}.${difficulty}`);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /** Begin (or restart) a run. Restarting mid-run is allowed — it IS the loop. */
  start(def: ChallengeDef, difficulty: string): ChallengeState {
    this.state = {
      def,
      running: true,
      timeLeftS: def.durationS,
      score: 0,
      streak: 0,
      finished: false,
      previousBest: this.best(def.id, difficulty),
      isNewBest: false,
    };
    this.difficulty = difficulty;
    return this.state;
  }

  private difficulty = '';

  get current(): ChallengeState | null { return this.state; }

  /** Advance the clock. Timed runs end at zero. */
  tick(dtS: number): void {
    const s = this.state;
    if (!s?.running || s.def.durationS === 0) return;
    s.timeLeftS = Math.max(0, s.timeLeftS - dtS);
    if (s.timeLeftS === 0) this.finish();
  }

  /** A goal landed. */
  onGoal(): void {
    const s = this.state;
    if (!s?.running) return;
    s.score += 1;
    s.streak += 1;
  }

  /** The drill reset WITHOUT a goal — a miss. Ends an untimed streak run. */
  onMiss(): void {
    const s = this.state;
    if (!s?.running) return;
    s.streak = 0;
    if (s.def.durationS === 0) this.finish();
  }

  /** Abandon without recording (player left the mode mid-run). */
  abandon(): void { this.state = null; }

  private finish(): void {
    const s = this.state;
    if (!s || s.finished) return;
    // An untimed (streak) run is scored by its LONGEST streak, which — because a
    // run ends on the first miss — is exactly `score`.
    s.running = false;
    s.finished = true;
    if (s.score > s.previousBest) {
      s.isNewBest = true;
      this.store.set(`${KEY_PREFIX}${s.def.id}.${this.difficulty}`, String(s.score));
    }
  }
}
